import { Modal } from "antd";
import React, { useState, useEffect, useMemo } from "react";
import RevstackDrilldownDrawerHeader from "./RevstackDrilldownDrawerHeader";
import { BsGraphUpArrow } from "react-icons/bs";
import { CA_DD_MODULES, DASHBOARD_TYPES, RevstackCASectionIds, RevstackDashboardModules, RevstackFilterTypes, RevstackFrequencySlicerOptions } from "@/ts/common-types";
import { resetRevstackDrilldownTrendsWidgetData, updateAiInsightsPayloadData, updateDrilldownFilters, updateRevstackDrilldownTrendsWidgetData, updateTrendsFilters } from "@features/revstackWidgetsSlice";
import { useDispatch, useSelector } from "react-redux";
import { useGetDashboardConfig, useGetRevstackDDTabsConfig, useRevstackStreamData } from "@hooks/AllCustomHooks";
import { ALL_OPTIONS, DATE_PICKER_PRESET_PERIODS, REVSTACK_GRANULARITY_DIVISORS, REVSTACK_GRANULARITY_TO_PERIOD_MAP, REVSTACK_PERIOD_TO_DAYS_MAP } from "@/ts/constants";
import { buildQueriesFromWidgetsByCubeId } from "@/ts/revstack/helpers";
import { getRevstackData } from "@/ts/retrieve-data";
import { flattenWidgetStructure } from "@/ts/revstack/renderers";

interface IRevstackDrilldownTrendsModal {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    titlePills: string[];
    filterState: any;
    filterDispatch: any;
    drilldownFilters: any;
    widgetData: any;
    drillDownActiveTab?: CA_DD_MODULES;
    activeSectionId: RevstackCASectionIds;
    module: RevstackDashboardModules;
    filters?: any[];
    defaultTab: CA_DD_MODULES;
}

// Default period when no match found
const DEFAULT_PERIOD_IN_DAYS = 7;

// Helper function to get base period in days from time period
const getBasePeriodInDays = (timePeriod: DATE_PICKER_PRESET_PERIODS): number => {
    return REVSTACK_PERIOD_TO_DAYS_MAP[timePeriod] || DEFAULT_PERIOD_IN_DAYS;
};

// Helper function to calculate period value based on granularity
const calculatePeriodValue = (basePeriodInDays: number, granularity: RevstackFrequencySlicerOptions): number => {
    const divisor = REVSTACK_GRANULARITY_DIVISORS[granularity] || 1;
    return Math.ceil(basePeriodInDays / divisor);
};

const RevstackDrilldownTrendsModal: React.FC<IRevstackDrilldownTrendsModal> = ({
    isOpen,
    title,
    onClose,
    titlePills,
    filterState,
    filterDispatch,
    drilldownFilters,
    widgetData,
    activeSectionId,
    module,
    defaultTab,
}) => {
    const [chartDimensions, setChartDimensions] = useState({
        height: 0,
        width: 0,
    });

    const trendsFilters = useSelector(
        (state: { trendsFilters: { value: any } }) =>
            state.trendsFilters.value
    );


    const drilldownTrendsModalConfig = useGetDashboardConfig(module, DASHBOARD_TYPES.DRILL_DOWN_TRENDS_MODAL, undefined, activeSectionId).widgetConfig;

    const tabsConfig = drilldownTrendsModalConfig?.[0]?.config?.tabs

    const [activeTab, setActiveTab] = useState(tabsConfig?.defaultTab || defaultTab || CA_DD_MODULES.OVERVIEW);
    // Shared time period and granularity settings across all tabs
    const [selectedTimePeriod, setSelectedTimePeriod] = useState<DATE_PICKER_PRESET_PERIODS>(DATE_PICKER_PRESET_PERIODS.LAST_30_DAYS);
    const [granularity, setGranularity] = useState<RevstackFrequencySlicerOptions>(RevstackFrequencySlicerOptions.Daily);
    // Shared filter data cache across all tabs to prevent unnecessary refetches
    const [pillFilterDropDownData, setPillFilterDropDownData] = useState<Record<string, any[]>>({});

    const dispatch = useDispatch();

    useEffect(() => {
        const updateHeight = () => {
            setChartDimensions({
                height: Math.min(document.documentElement.clientHeight - 100, 675),
                width: Math.min(document.documentElement.clientWidth - 100, 1200),
            });
        };

        updateHeight();
        window.addEventListener("resize", updateHeight);

        return () => window.removeEventListener("resize", updateHeight);
    }, []);

    const drilldownModalConfig = useGetDashboardConfig(module, DASHBOARD_TYPES.DRILL_DOWN_TRENDS_MODAL, undefined, activeSectionId).widgetConfig;

    const primaryFilter = useMemo(() => {
        const filterKey = drilldownTrendsModalConfig[0]?.config?.header?.components?.find((component: any) => component.section === "main")?.elements?.[0]?.filterKey || "";
        const filterValue = trendsFilters.selected?.[filterKey];
        return {
            filterValue,
            filterKey,
        }
    }, [trendsFilters.selected, drilldownTrendsModalConfig]);

    const secondaryFilter = useMemo(() => {
        const elements = drilldownTrendsModalConfig[0]?.config?.header?.components?.find((component: any) => component.section === "filters")?.elements || [];

        const filterKeys = elements.map((element: any) => element?.filterKey || "");
        const filterValues = filterKeys.map((key: string) => trendsFilters.selected?.[key]);

        return {
            filterKey: filterKeys,
            filterValue: filterValues,
        };
    }, [trendsFilters.selected, drilldownTrendsModalConfig]);

    const onPrimaryFilterChange = (value: any) => {
        dispatch(
            updateTrendsFilters({
                selected: {
                    ...trendsFilters?.selected,
                    [primaryFilter?.filterKey]:
                        value === ALL_OPTIONS.value
                            ? [ALL_OPTIONS.value]
                            : [parseInt(value, 10)],
                },
            })
        );
    };

    const onSecondaryFilterChange = (value: any, filterIndex?: number) => {
        if (secondaryFilter?.filterKey && Array.isArray(secondaryFilter.filterKey) && secondaryFilter.filterKey.length > 1) {
            // Multiple filters case
            if (filterIndex !== undefined && filterIndex < secondaryFilter.filterKey.length) {
                const filterKey = secondaryFilter.filterKey[filterIndex];
                // Handle array values for multiSelect
                let processedValue = value;
                if (Array.isArray(value)) {
                    // For multiSelect, use the array as is
                    processedValue = value;
                } else {
                    // For single select, wrap in array
                    processedValue = value === ALL_OPTIONS.value ? [ALL_OPTIONS.value] : [value];
                }
                
                // Check if the new value is different from the current value
                const currentValue = trendsFilters?.selected?.[filterKey];
                const isValueChanged = JSON.stringify(currentValue) !== JSON.stringify(processedValue);
                
                if (isValueChanged) {
                    dispatch(
                        updateTrendsFilters({
                            selected: {
                                ...trendsFilters?.selected,
                                [filterKey]: processedValue,
                            },
                        })
                    );
                }
            }
        } else if (secondaryFilter?.filterKey?.length === 1) {
            // Single filter case
            const filterKey = secondaryFilter.filterKey[0];
            
            // Handle array values for multiSelect
            let processedValue = value;
            if (Array.isArray(value)) {
                // For multiSelect, use the array as is
                processedValue = value;
            } else {
                // For single select, wrap in array
                processedValue = value === ALL_OPTIONS.value ? [ALL_OPTIONS.value] : [value];
            }
            
            // Check if the new value is different from the current value
            const currentValue = trendsFilters?.selected?.[filterKey];
            const isValueChanged = JSON.stringify(currentValue) !== JSON.stringify(processedValue);
            
            if (isValueChanged) {
                dispatch(
                    updateTrendsFilters({
                        selected: {
                            ...trendsFilters?.selected,
                            [filterKey]: processedValue,
                        },
                    })
                );
            }
        }
    };


    const primaryFilterOptions = useMemo(
        () =>
            (trendsFilters?.options?.[primaryFilter?.filterKey] || []).map(
                (subcat: any) => ({
                    label: subcat.label,
                    value: subcat.value,
                })
            ),
        [trendsFilters?.options?.[primaryFilter?.filterKey]]
    );

    const secondaryFilterOptions = useMemo(() => {
        if (!secondaryFilter?.filterKey || secondaryFilter.filterKey.length === 0) {
            return undefined;
        }

        if (secondaryFilter.filterKey.length === 1) {
            // Single filter - return single object
            return trendsFilters?.options?.[secondaryFilter.filterKey[0]];
        }

        // Multiple filters - return array of filter options
        return secondaryFilter.filterKey.map((filterKey: string) =>
            trendsFilters?.options?.[filterKey]
        );
    }, [trendsFilters?.options, secondaryFilter?.filterKey]);


    const selectedOption = primaryFilterOptions?.find((option: any) =>
        primaryFilter?.filterValue?.includes(option.value)
    );

    const selectedSecondaryOption = useMemo(() => {
        if (!secondaryFilterOptions || !secondaryFilter?.filterValue) return undefined;

        if (secondaryFilter?.filterKey && Array.isArray(secondaryFilter.filterKey) && secondaryFilter.filterKey.length > 1) {
            // Multiple filters case - return array of selected options
            return secondaryFilterOptions.map((options: any[], index: number) => {
                const filterValue = secondaryFilter.filterValue[index];
                return options?.find((option: any) =>
                    Array.isArray(filterValue) ? filterValue.includes(option.value) : filterValue === option.value
                );
            });
        } else {
            // Single filter case
            const filterValue = secondaryFilter.filterValue[0];
            return secondaryFilterOptions?.find((option: any) =>
                Array.isArray(filterValue) ? filterValue.includes(option.value) : filterValue === option.value
            );
        }
    }, [secondaryFilterOptions, secondaryFilter?.filterValue]);

    const selectedPrimaryFilterLabel = selectedOption?.label;
    const selectedSecondaryLabel = useMemo(() => {
        if (!selectedSecondaryOption) return "";

        if (secondaryFilter?.filterKey && Array.isArray(secondaryFilter.filterKey) && secondaryFilter.filterKey.length > 1) {
            // Multiple filters - return array of labels
            return selectedSecondaryOption.map((option: any) => option?.label || "");
        } else {
            // Single filter
            return selectedSecondaryOption?.label || "";
        }
    }, [selectedSecondaryOption]);

    const { getRevstackWidgetStreamData } = useRevstackStreamData({
        config: drilldownModalConfig,
        streamConfig: {
            resetWidgetDataAction: resetRevstackDrilldownTrendsWidgetData,
            updateWidgetDataAction: updateRevstackDrilldownTrendsWidgetData,
            updateAiInsightsAction: updateAiInsightsPayloadData,
            aiInsightsKey: "drilldownTrends",
        },
        overviewTabKey: CA_DD_MODULES.OVERVIEW,
    });

    const selectedDateRange = useSelector((state: any) => state.selectedDateRange.value);

    const processSections = async (
        sections: any[],
        cubeDateObject: any,
        granularityValue: RevstackFrequencySlicerOptions,
        periodValue: number,
        isSeparateCall= false
    ) => {
        // Process all sections and collect all queries
        const allQueries: any[] = [];
        const resultKeysToReset: string[] = [];

        // Process all sections
        sections.forEach((sectionData: any) => {
            // Update relativePeriodConfig for all widgets in this section
            if (sectionData.config?.widgets) {
                // Extract all actual widget objects from the nested structure
                const allWidgets = flattenWidgetStructure(sectionData.config.widgets);

                // Update relativePeriodConfig for all widgets in this section
                allWidgets.forEach((widget: any) => {
                    if (widget.relativePeriodConfig) {
                        widget.relativePeriodConfig.granularity = granularityValue;
                        widget.relativePeriodConfig.value = periodValue;
                    }
                });

                // Extract result key for state management
                const resultKey = allWidgets.find(
                    (widget: any) => widget.queryMetaInfo?.resultKey
                )?.queryMetaInfo?.resultKey;

                if (resultKey) {
                    resultKeysToReset.push(resultKey);
                }

                // Build cube queries for this section using flattened widgets
                const allCubeQueries = buildQueriesFromWidgetsByCubeId(
                    allWidgets,
                    cubeDateObject
                );

                allQueries.push(...allCubeQueries);
            }
        });

        if (allQueries.length === 0) {
            const sectionType = isSeparateCall ? "overview section" : "other sections";
            console.warn(`No data queries generated for ${sectionType}`);
            return;
        }

        // Reset all result keys before API call
        resultKeysToReset.forEach(resultKey => {
            dispatch(updateRevstackDrilldownTrendsWidgetData({ 
                [resultKey]: null,
                [`${resultKey}_percent`]: null,
                [`${resultKey}_non_percent`]: null
            }));
        });

        
        await getRevstackData({
            cubeQueries: allQueries,
            onStreamData: (streamData: any) => {
                // Update widget data with streaming results
                dispatch(updateRevstackDrilldownTrendsWidgetData(streamData));
            }
        });
    };

    const handlePeriodAndGranularityDataFetch = async (
        timePeriod: DATE_PICKER_PRESET_PERIODS,
        granularityValue: RevstackFrequencySlicerOptions,
        configSessionKey= "drilldowntrendsmodal-config"
    ) => {
        // Check if granularity changed (but period didn't change from user selection)
        const granularityChanged = granularityValue !== granularity;
        const periodChanged = timePeriod !== selectedTimePeriod;
        
        // Only auto-adjust period if granularity changed but period wasn't manually changed
        let adjustedTimePeriod = timePeriod;
        let basePeriodInDays: number;

        if (granularityChanged && !periodChanged) {
            // Automatically set period based on granularity only when granularity changes
            const defaultPeriod = REVSTACK_GRANULARITY_TO_PERIOD_MAP[granularityValue];
            if (defaultPeriod) {
                adjustedTimePeriod = defaultPeriod;
                basePeriodInDays = getBasePeriodInDays(defaultPeriod);
            } else {
                // Fallback to manually selected period if granularity not mapped
                basePeriodInDays = getBasePeriodInDays(timePeriod);
            }
        } else {
            // Use the manually selected period
            basePeriodInDays = getBasePeriodInDays(timePeriod);
        }

        // Update shared state
        setSelectedTimePeriod(adjustedTimePeriod);
        setGranularity(granularityValue);

        try {
            // Get session widget config
            const sessionWidgetConfig = JSON.parse(
                sessionStorage.getItem(configSessionKey) || "[]"
            );

            if (!sessionWidgetConfig || sessionWidgetConfig.length === 0) {
                console.error("Failed to get trends configuration data");
                return;
            }

            // Prepare cube date object
            const { compareDateRange, dateRange } = selectedDateRange;
            const cubeDateObject = {
                currentRange: [dateRange.start, dateRange.end],
                previousRange: [compareDateRange.start, compareDateRange.end],
            };

            // Calculate the period value based on granularity
            const periodValue = calculatePeriodValue(basePeriodInDays, granularityValue);

            // Separate the overview section from other sections
            const overviewSection = sessionWidgetConfig.find(
                (section: any) => section.sectionKey === "drill-down-trends-subcategory-overview-analysis"
            );

            const otherSections = sessionWidgetConfig.filter(
                (section: any) => section.sectionKey !== "drill-down-trends-subcategory-overview-analysis"
            );

            // Process overview section separately if it exists
            if (overviewSection) {
                await processSections(
                    [overviewSection],
                    cubeDateObject,
                    granularityValue,
                    periodValue,
                    true // isSeparateCall = true for overview section
                );
            }

            // Process other sections together if they exist
            if (otherSections.length > 0) {
                await processSections(
                    otherSections,
                    cubeDateObject,
                    granularityValue,
                    periodValue,
                    false // isSeparateCall = false for other sections
                );
            }

        } catch (error: any) {
            console.error("Error in handlePeriodAndGranularityDataFetch:", error);
        }
    };

    const handlePeriodAndGranularityChange = (timePeriod: DATE_PICKER_PRESET_PERIODS, granularity: RevstackFrequencySlicerOptions) => {
        handlePeriodAndGranularityDataFetch(timePeriod, granularity);
    };

    const tabItems = useGetRevstackDDTabsConfig({
        drilldownConfig: drilldownTrendsModalConfig,
        activeTab,
        filterState,
        filterDispatch,
        drilldownFilters,
        widgetData,
        layoutType: "drilldownTrends",
        selectedTimePeriod: selectedTimePeriod,
        granularity: granularity,
        handlePeriodAndGranularityChange,
        pillFilterDropDownData,
        setPillFilterDropDownData,
    });

    const items = tabItems
        .map(({ key, label, disabled }) => ({
            key,
            label,
            disabled,
        }));

    const tabContentMap = tabItems.reduce((acc, { key, children }) => {
        acc[key] = children;
        return acc;
    }, {} as Record<string, React.ReactNode>);

    useEffect(() => {
        if (isOpen) {
            getRevstackWidgetStreamData();
        }
    }, [isOpen, drilldownFilters, trendsFilters]);

    return (
        <Modal
            open={isOpen}
            onCancel={onClose}
            className="revstack-dashboard-scope"
            title={
                <RevstackDrilldownDrawerHeader
                    title={title || "Trends Analysis"}
                    titleIcon={<BsGraphUpArrow size={16} />}
                    setActiveTab={(activeKey) =>
                        setActiveTab(activeKey as CA_DD_MODULES)
                    }
                    {...{
                        primaryFilterOptions,
                        secondaryFilterOptions,
                        secondaryFilterValue: secondaryFilter?.filterValue,
                        primaryFilterValue: primaryFilter?.filterValue,
                        onPrimaryFilterChange,
                        onSecondaryFilterChange,
                        selectedPrimaryFilterLabel,
                        selectedSecondaryLabel,
                        activeTab,
                        items,
                    }}
                    titlePills={titlePills}
                    isAdvancedChartsRequired={false}
                    config={drilldownTrendsModalConfig?.[0]?.config || {}}
                />
            }
            width={1300}
            height={700}
            wrapClassName="revstack-dd-trends-modal"
            footer={null}
            style={{ top: 10 }}
            styles={{
                header: { padding: "0px" },
                body: {
                    padding: 0,
                    backgroundColor: "#F0F4F8",
                    overflowY: "scroll",
                },
            }}
        >
            <div className="p-4">
                {/* <h1>Coming Soon...</h1> */}
                {tabContentMap[activeTab] || null}
            </div>
        </Modal>
    );
};

export default RevstackDrilldownTrendsModal;